
import { MessageCircle } from "lucide-react";
import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";

interface WhatsAppChatProps {
  phoneNumber: string;
  message?: string;
}

const WhatsAppChat = ({ phoneNumber, message = "Hello, I have a question about Benchr." }: WhatsAppChatProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  // Format the phone number to remove any non-digit characters
  const formattedPhoneNumber = phoneNumber.replace(/\D/g, "");
  
  // Get the current page title
  const getPageTitle = () => {
    switch(location.pathname) {
      case '/':
        return "Benchr.co - E-commerce Competitive Intelligence";
      case '/about':
        return "À propos de Benchr - E-commerce Competitive Intelligence";
      case '/case-studies':
        return "Client Success Stories - Benchr";
      case '/product-example':
        return "Tech Product Display - Benchr";
      default:
        return "Benchr.co";
    }
  };
  
  // WhatsApp chat URL with encoded message including the page title
  const whatsappUrl = `https://wa.me/${formattedPhoneNumber}?text=${encodeURIComponent(`${message} (${getPageTitle()} - ${window.location.href})`)}`;

  // Show the button after a short delay and when user scrolls
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 1500);

    const handleScroll = () => {
      if (window.scrollY > 300 && !isVisible) {
        setIsVisible(true);
      }
    };

    window.addEventListener("scroll", handleScroll);
    
    return () => {
      clearTimeout(timer);
      window.removeEventListener("scroll", handleScroll);
    };
  }, [isVisible]);

  const togglePopup = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div className="fixed bottom-6 right-6 z-50">
      {/* Popup dialog */}
      {isOpen && (
        <div className="bg-white dark:bg-benchr-800 rounded-lg shadow-xl mb-4 w-72 transform transition-transform duration-300 scale-100 origin-bottom-right animate-fade-in">
          <div className="bg-green-500 text-white p-4 rounded-t-lg flex items-center">
            <div className="flex items-center">
              {/* Benchr logo */}
              <div className="bg-white rounded-full p-1 flex-shrink-0 mr-3">
                <img 
                  src="/lovable-uploads/b83fc8ce-9261-4964-8596-d15b9bbb6831.png" 
                  alt="Benchr Logo" 
                  className="w-8 h-8 object-contain"
                />
              </div>
              <div>
                <h3 className="font-semibold text-sm">Need help?</h3>
                <p className="text-xs opacity-90">Our team is available on WhatsApp</p>
              </div>
            </div>
          </div>
          <div className="p-4">
            <p className="text-sm text-benchr-700 dark:text-benchr-200 mb-4">
              Ask us your questions directly via WhatsApp! Our team will respond as soon as possible.
            </p>
            <a 
              href={whatsappUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className="block w-full md:w-auto px-6 py-3 bg-green-500 hover:bg-green-600 text-white text-center rounded-md font-medium transition-colors"
            >
              Start a conversation
            </a>
          </div>
        </div>
      )}

      {/* WhatsApp floating button */}
      <button
        onClick={togglePopup}
        className={`bg-green-500 hover:bg-green-600 text-white rounded-full w-14 h-14 flex items-center justify-center shadow-lg transition-all duration-500 ${
          isVisible ? "translate-y-0 opacity-100" : "translate-y-16 opacity-0"
        }`}
        aria-label="Chat on WhatsApp"
      >
        <MessageCircle size={28} />
      </button>
    </div>
  );
};

export default WhatsAppChat;
